import type { Outputs } from "@postdaily/api/routers/index";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button, buttonVariants } from "@postdaily/ui/components/button";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, BotIcon } from "lucide-react";
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useEffect } from "react";

import { PlatformConnectList } from "@/components/channels/platform-connect-list";
import { PageHeader } from "@/components/page-header";
import { useConnectProvider } from "@/hooks/use-connect-provider";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { useUpgradeDialogStore } from "@/stores/upgrade-dialog-store";

export const Route = createFileRoute("/(app)/channels/connect")({
  component: RouteComponent,
});

type Workspace = NonNullable<
  ReturnType<typeof useCurrentWorkspace>["currentWorkspace"]
>;

function RouteComponent() {
  const { currentWorkspace, isLoading, setCurrentWorkspaceId, workspaces } =
    useCurrentWorkspace();
  // An AI app's connect link names its workspace (`list_channels`, and the
  // consent screen's new tab). Switch to it only when the signed-in person
  // belongs to it, and hold the page until the switch lands so a channel is
  // never connected to the wrong workspace. Same as /media's upload link.
  const [{ workspace: linkedWorkspaceId }] = useQueryStates({
    workspace: parseAsString,
  });
  const linkedWorkspace = linkedWorkspaceId
    ? workspaces.find((workspace) => workspace.id === linkedWorkspaceId)
    : undefined;

  useEffect(() => {
    if (linkedWorkspace && currentWorkspace?.id !== linkedWorkspace.id) {
      setCurrentWorkspaceId(linkedWorkspace.id);
    }
  }, [currentWorkspace?.id, linkedWorkspace, setCurrentWorkspaceId]);

  if (
    !currentWorkspace ||
    (linkedWorkspace && currentWorkspace.id !== linkedWorkspace.id)
  ) {
    return <Skeleton className="h-72 w-full" />;
  }

  return (
    <ConnectHub
      linkedWorkspaceMissing={
        Boolean(linkedWorkspaceId) && !isLoading && !linkedWorkspace
      }
      workspace={currentWorkspace}
    />
  );
}

function ConnectHub({
  linkedWorkspaceMissing,
  workspace,
}: {
  /** The page was opened from a connect link for a workspace you can't use. */
  linkedWorkspaceMissing: boolean;
  workspace: Workspace;
}) {
  const openUpgradeDialog = useUpgradeDialogStore(
    (store) => store.openUpgradeDialog,
  );
  const {
    accountsQuery,
    isPending,
    isError,
    isControlsError,
    isAtLimit,
    limits,
    planName,
  } = useConnectProvider({ workspace });

  const accounts: Outputs["socialAccounts"]["list"] = accountsQuery.data ?? [];
  // Set by an AI app's link (`list_channels`) and the consent screen's new
  // tab: the person just connected their AI app and came here from it.
  // "onboarding" is the wizard's exit when no channel was connected.
  const [{ from }] = useQueryStates({
    from: parseAsStringLiteral(["agent", "onboarding"]),
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Connect a channel"
        description="Add the social accounts you want to schedule and publish to."
        actions={
          <Link
            to="/channels"
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            <ArrowLeftIcon data-icon="inline-start" />
            All channels
          </Link>
        }
      />

      {linkedWorkspaceMissing ? (
        <Alert variant="destructive">
          <AlertTitle>This connect link is for another workspace</AlertTitle>
          <AlertDescription>
            You aren't a member of the workspace your AI app asked for, so a
            channel connected here would go to {workspace.name} instead. Check
            which PostDaily account you're signed in with, or ask the app for a
            new link.
          </AlertDescription>
        </Alert>
      ) : from === "agent" && accounts.length === 0 ? (
        <Alert>
          <BotIcon />
          <AlertTitle>Your AI app is connected</AlertTitle>
          <AlertDescription>
            Connect a social account to {workspace.name} below so it has
            somewhere to post. Then go back to your AI app and ask it again.
          </AlertDescription>
        </Alert>
      ) : from === "onboarding" && accounts.length === 0 ? (
        // Skipping setup on its connect step lands here, on another connect
        // page; without a word it reads as a skip that did nothing.
        <Alert>
          <AlertTitle>Setup skipped</AlertTitle>
          <AlertDescription>
            Connect a channel whenever you're ready. PostDaily can't schedule or
            publish anything until one is connected.
          </AlertDescription>
        </Alert>
      ) : null}

      {isAtLimit ? (
        <Alert>
          <AlertTitle>Channel limit reached</AlertTitle>
          <AlertDescription>
            {planName} includes {limits?.maxSocialAccounts}{" "}
            {limits?.maxSocialAccounts === 1 ? "channel" : "channels"}. Upgrade
            to connect more.
          </AlertDescription>
          <AlertAction>
            <Button
              onClick={() => openUpgradeDialog({ source: "channel_limit" })}
              size="sm"
              type="button"
              variant="outline"
            >
              Upgrade
            </Button>
          </AlertAction>
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertTitle>Channels unavailable</AlertTitle>
          <AlertDescription>{accountsQuery.error?.message}</AlertDescription>
        </Alert>
      ) : null}

      {isControlsError ? (
        <Alert variant="destructive">
          <AlertTitle>Connection controls unavailable</AlertTitle>
          <AlertDescription>
            Unable to load provider controls. Try again.
          </AlertDescription>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <PlatformConnectList workspace={workspace} accounts={accounts} />
      )}
    </div>
  );
}
